import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounced } from '@/hooks/useDebounced';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/synapse/Icon';
import { PaginationFooter } from '@/components/synapse/PaginationFooter';
import { Select } from '@/components/synapse/Select';
import { StatusDot } from '@/components/synapse/StatusDot';
import { TypePill } from '@/components/synapse/TypePill';
import type { IconName } from '@/components/synapse/Icon';
import { BatchRow } from '@/components/synapse/BatchRow';
import { useItemDetail, useStockList } from '@/hooks/useInventory';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import { DEFAULT_EXPIRING_WITHIN_DAYS, expiryStatus } from '@/lib/expiry';
import type { ExpiryStatusFilter, Item, ItemType, StockStatusFilter } from '@/types/api';

const TYPE_ICON: Record<ItemType, IconName> = {
  medication: 'pill',
  consumable: 'box',
  medical_device: 'device',
  reagent: 'flask',
  other: 'box_in',
};

type ExpiryChip = 'all' | ExpiryStatusFilter;

const PAGE_SIZE = 25;

export function StockStatusPage() {
  const { t } = useTranslation();
  // `?filter=out|expiring|expired` are entry-points from the home-page
  // summary cards. Captured once via useRef so strict-mode double-invoke can't
  // re-seed state from a stale URL; cleared from the URL on mount so back/
  // forward navigation doesn't re-apply the filter later.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilterRef = useRef(searchParams.get('filter'));
  const initialFilter = initialFilterRef.current;

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | ItemType>('');
  const [stockStatus, setStockStatus] = useState<'' | StockStatusFilter>(
    initialFilter === 'out' ? 'out_of_stock' : '',
  );
  const [expiryChip, setExpiryChip] = useState<ExpiryChip>(
    initialFilter === 'expiring'
      ? 'expiring_soon'
      : initialFilter === 'expired'
      ? 'expired'
      : 'all',
  );
  const [page, setPage] = useState(1);
  const debouncedQuery = useDebounced(query.trim(), 200);

  useEffect(() => {
    if (searchParams.get('filter')) {
      const next = new URLSearchParams(searchParams);
      next.delete('filter');
      setSearchParams(next, { replace: true });
    }
    // Only consume on mount; subsequent state edits are independent of the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any filter change should snap back to the first page so the user doesn't
  // land on an out-of-range offset (e.g. page 4 of a now-1-page result).
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, typeFilter, expiryChip, stockStatus]);

  const { data, isLoading, isError, error, refetch, isFetching } = useStockList({
    query: debouncedQuery || undefined,
    type: typeFilter || undefined,
    stockStatus: stockStatus || undefined,
    expiryStatus: expiryChip === 'all' ? undefined : expiryChip,
    expiringWithinDays: DEFAULT_EXPIRING_WITHIN_DAYS,
    page,
    pageSize: PAGE_SIZE,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtersActive =
    debouncedQuery.length > 0 ||
    typeFilter !== '' ||
    expiryChip !== 'all' ||
    stockStatus !== '';
  const noData = !isLoading && !isError && total === 0;
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE);

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-col gap-1.5 sm:mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">{t('stock.title')}</h1>
        <p className="text-[13.5px] text-muted">{t('stock.subtitle')}</p>
      </header>

      <div className="mb-4 flex flex-col gap-3 sm:mb-5 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('stock.searchPlaceholder')}
            className="pl-9"
            aria-label={t('stock.search')}
          />
        </div>
        <div className="flex gap-2 sm:w-auto">
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as '' | ItemType)}
            aria-label={t('stock.filterByType')}
            className="min-w-[140px]"
          >
            <option value="">{t('stock.allTypes')}</option>
            <option value="medication">{t('itemType.medication')}</option>
            <option value="consumable">{t('itemType.consumable')}</option>
            <option value="medical_device">{t('itemType.medical_device')}</option>
            <option value="reagent">{t('itemType.reagent')}</option>
            <option value="other">{t('itemType.other')}</option>
          </Select>
        </div>
      </div>

      <div
        className="mb-5 flex flex-wrap gap-2"
        role="group"
        aria-label={t('stock.filterByExpiry')}
      >
        {(
          [
            { value: 'all', label: t('stock.filterAll') },
            {
              value: 'expiring_soon',
              label: t('stock.filterExpiringWithDays', { days: DEFAULT_EXPIRING_WITHIN_DAYS }),
            },
            { value: 'expired', label: t('stock.filterExpired') },
          ] as const
        ).map((chip) => {
          const active = expiryChip === chip.value;
          return (
            <button
              key={chip.value}
              type="button"
              aria-pressed={active}
              onClick={() => setExpiryChip(chip.value)}
              className={cn(
                'inline-flex h-8 items-center rounded-full px-3 text-[12.5px] font-medium transition-colors',
                active
                  ? 'bg-accent text-accent-foreground'
                  : 'border border-line bg-paper text-muted hover:bg-paper-2 hover:text-ink',
              )}
            >
              {chip.label}
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={stockStatus === 'out_of_stock'}
          onClick={() =>
            setStockStatus((s) => (s === 'out_of_stock' ? '' : 'out_of_stock'))
          }
          className={cn(
            'inline-flex h-8 items-center rounded-full px-3 text-[12.5px] font-medium transition-colors',
            stockStatus === 'out_of_stock'
              ? 'bg-accent text-accent-foreground'
              : 'border border-line bg-paper text-muted hover:bg-paper-2 hover:text-ink',
          )}
        >
          {t('stock.outOfStock')}
        </button>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : noData && !filtersActive ? (
        <EmptyState />
      ) : noData ? (
        <FilteredEmptyState
          onClear={() => {
            setQuery('');
            setTypeFilter('');
            setExpiryChip('all');
            setStockStatus('');
          }}
        />
      ) : (
        <Card className={cn('overflow-hidden p-0', isFetching && 'opacity-95')}>
          <ul className="divide-y divide-line">
            {items.map((item) => (
              <li key={item.id}>
                <ItemRow item={item} />
              </li>
            ))}
          </ul>
          <PaginationFooter
            page={page}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </Card>
      )}
    </div>
  );
}

// ---------------- subcomponents ----------------

function LoadingState() {
  return (
    <Card className="overflow-hidden p-0" aria-busy="true" aria-live="polite">
      <ul className="divide-y divide-line">
        {[0, 1, 2, 3, 4].map((i) => (
          <li key={i} className="h-[64px] animate-pulse bg-paper-2" />
        ))}
      </ul>
    </Card>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="box" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('stock.noItemsYet')}</div>
      <p className="max-w-[320px] text-[12.5px] text-muted">{t('stock.onceStockComes')}</p>
      <Button asChild>
        <Link to="/add">
          <Icon name="plus" size={16} /> {t('nav.addStock')}
        </Link>
      </Button>
    </Card>
  );
}

function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="search" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('stock.noItemsMatch')}</div>
      <p className="max-w-[320px] text-[12.5px] text-muted">{t('stock.tryDifferent')}</p>
      <Button variant="ghost" onClick={onClear}>
        {t('stock.clearFilters')}
      </Button>
    </Card>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  const message = error instanceof ApiError ? translateApiError(error) : t('stock.couldNotLoadStockMsg');
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive-bg text-destructive">
        <Icon name="alert" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('stock.couldNotLoadStock')}</div>
      <p className="max-w-[320px] text-[12.5px] text-muted">{message}</p>
      <Button onClick={onRetry}>{t('common.tryAgain')}</Button>
    </Card>
  );
}


interface ItemRowProps {
  item: Item;
}

function ItemRow({ item }: ItemRowProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showDepleted, setShowDepleted] = useState(false);
  const panelId = useId();
  const oos = item.totalStock <= 0;

  const expiryTone: 'ok' | 'warn' | 'danger' | 'neutral' = oos
    ? 'neutral'
    : item.expiredBatchCount > 0
    ? 'danger'
    : item.expiringSoonBatchCount > 0
    ? 'warn'
    : item.earliestExpiryDate
    ? 'ok'
    : 'neutral';

  const expiryLabel = oos
    ? null
    : item.expiredBatchCount > 0
    ? t('stock.nExpired', { count: item.expiredBatchCount })
    : item.expiringSoonBatchCount > 0
    ? t('stock.nExpiringSoon', { count: item.expiringSoonBatchCount })
    : item.earliestExpiryDate
    ? t('stock.nextExp', { date: item.earliestExpiryDate })
    : t('issue.noExpiry');

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-paper-2"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper-2 text-muted">
          <Icon name={TYPE_ICON[item.type]} size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[14px] font-semibold tracking-[-0.01em]">{item.brandName}</span>
            {item.genericName && (
              <span className="text-[13px] text-muted">{item.genericName}</span>
            )}
            {item.strength && (
              <span className="font-mono text-[13px] text-ink-2 tabular-nums">
                · {item.strength}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge tone={oos ? 'danger' : 'success'} size="sm" dot>
              {oos ? t('stock.outOfStock') : t('stock.inStock')}
            </Badge>
            <TypePill type={item.type} size="sm" />
            {expiryLabel && (
              <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted">
                <StatusDot tone={expiryTone} />
                <span
                  className={cn(
                    expiryTone === 'danger'
                      ? 'text-destructive'
                      : expiryTone === 'warn'
                      ? 'text-warn'
                      : 'text-muted',
                  )}
                >
                  {expiryLabel}
                </span>
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span
            className={cn(
              'font-mono text-[15px] font-semibold tabular-nums',
              oos ? 'text-destructive' : 'text-ink',
            )}
          >
            {item.totalStock}
          </span>
          <span className="text-[11px] text-muted">{oos ? t('stock.zeroOnHand') : t('stock.onHand')}</span>
        </div>
        <Icon
          name={expanded ? 'chevron_up' : 'chevron_down'}
          size={16}
          className="ml-1 mt-1 shrink-0 text-muted"
        />
      </button>
      {expanded && !oos && (
        <ExpandedBatches
          id={panelId}
          itemId={item.id}
          showDepleted={showDepleted}
          onToggleDepleted={() => setShowDepleted((v) => !v)}
        />
      )}
      {expanded && oos && (
        <div
          id={panelId}
          className="border-t border-line bg-paper-2 px-4 py-3 text-[12.5px] text-muted"
        >
          {t('stock.noStockedBatchesItem')}{' '}
          <Link to="/add" className="text-ink underline">
            {t('stock.addStockLink')}
          </Link>{' '}
          {t('stock.bringItBack')}
        </div>
      )}
    </div>
  );
}

interface ExpandedBatchesProps {
  id: string;
  itemId: string;
  showDepleted: boolean;
  onToggleDepleted: () => void;
}

function ExpandedBatches({ id, itemId, showDepleted, onToggleDepleted }: ExpandedBatchesProps) {
  const { t } = useTranslation();
  // `enabled: true` is the default but stated explicitly so the lazy intent
  // (fetch only once expanded; component unmounts on collapse) is visible at
  // the call site rather than implied by the parent's conditional render.
  const { data: detail, isLoading, isError, error } = useItemDetail(itemId, { enabled: true });

  if (isLoading) {
    return (
      <div id={id} className="border-t border-line bg-paper-2 px-4 py-3">
        <div className="h-10 animate-pulse rounded-md bg-paper" />
      </div>
    );
  }
  if (isError) {
    const msg = error instanceof ApiError ? translateApiError(error) : t('stock.couldNotLoadBatches');
    return (
      <div
        id={id}
        className="border-t border-line bg-paper-2 px-4 py-3 text-[12.5px] text-destructive"
      >
        {msg}
      </div>
    );
  }
  if (!detail) return null;

  const visible = showDepleted ? detail.batches : detail.batches.filter((b) => b.balance > 0);
  const depletedCount = detail.batches.length - detail.batches.filter((b) => b.balance > 0).length;

  return (
    <div id={id} className="border-t border-line bg-paper-2 px-4 py-3">
      {visible.length === 0 ? (
        <div className="text-[12.5px] text-muted">{t('stock.noBatchesShow')}</div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {visible.map((b) => {
            const status = expiryStatus(b.expiryDate, DEFAULT_EXPIRING_WITHIN_DAYS);
            return (
              <li
                key={b.id}
                className={cn(
                  'flex items-center rounded-md border border-line bg-paper px-3 py-2',
                  b.balance === 0 && 'opacity-60',
                )}
              >
                <BatchRow batch={b} withinDays={DEFAULT_EXPIRING_WITHIN_DAYS} />
                {status === 'expired' && b.balance > 0 && (
                  <Link
                    to="/adjust"
                    className="ml-3 shrink-0 text-[11.5px] text-ink underline hover:no-underline"
                  >
                    {t('stock.discard')}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {depletedCount > 0 && (
        <button
          type="button"
          onClick={onToggleDepleted}
          className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-muted hover:text-ink"
        >
          <Icon name={showDepleted ? 'chevron_up' : 'chevron_down'} size={12} />
          {showDepleted
            ? t('stock.hideDepleted')
            : t('stock.showDepleted', {
                count: depletedCount,
                batchLabel: t('common.batch', { count: depletedCount }),
              })}
        </button>
      )}
    </div>
  );
}
